import React, { useMemo } from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import { styled } from 'baseui';
import type { Logger } from '../..';
import type { SendEventT } from '../../analytics/types';
import type { GetFeatureFlagFunctionT } from '../../common/providers/feature-flag-provider';
import { type ReviewOutputDataT } from '../../common/review-mode-side-panel';
import type { GetSignedUrlT, RegisterHotkeysArgsT, RegisterHotkeysResultT } from '../../common/types/types';
import type { AnnotationLabelT, BoundingBoxTrackingInputT, BoundingBoxTrackingOutputT } from '../../image';
import type { InputDataT, onStoreChangeT, SuperpixelImageRegionsDataT } from '../../image/labelling';
import { getItemFromStorage, setItemInStorage } from '../../image/labelling/utils';
import ImageLabelStudioModernized from '../../image/labelling-v2/studio-modernized';
import type { SamModelImagePluginRequestT, SamModelPluginRequestT, SAMSegmentImageData, SAMSegmentVideoData, StudioRef, TrackObjectsOutputT, TrackObjectsRequestT } from '../../image/types';
import { FEATURE_FLAGS } from '../consts/constants';
import { DEFAULT_REVIEW_MODE_FEATURE_CONFIG_ULABEL_LITE, REVIEW_MODE_VERSION_ULABEL_LITE } from '../consts/review-mode';
import type { SuggestionSidePanelPropsT } from '../types/types';
import { getFeatureFlags } from '../utils/feature';
const ImageStudioContainer = styled<'div', {
  $isFullWidth?: boolean;
  $showSuggestionsSidePanel?: boolean;
}>('div', ({
  $isFullWidth,
  $theme,
  $showSuggestionsSidePanel
}) => ({
  width: $isFullWidth ? `calc(100% - 30px)` : $showSuggestionsSidePanel ? 'calc(100% - 400px)' : 'calc(100% - 300px)',
  transition: `width ${$theme.animation.timing300}`
}));
export type StudioContainerPropsT = {
  inputData: InputDataT;
  annotationLabels: AnnotationLabelT[];
  onStoreChange?: onStoreChangeT;
  sendEvent?: SendEventT;
  onReviewStateChange?: (reviewOutput: ReviewOutputDataT) => void;
  onTrackingActiveChange?: (isActive: boolean) => void;
  fetchQuestionnaire: (questionnaireId: string) => Promise<QnAStateType>;
  setItemInStorage: (key: string, value: string, isPersistent: boolean) => void;
  getItemFromStorage: (key: string, isPersistent: boolean) => string;
  getSuperpixelImageRegionsData?: () => Promise<SuperpixelImageRegionsDataT>;
  isFeatureEnabled: GetFeatureFlagFunctionT;
  renderPDFFrame?: (currentPage: number, canvasContext: CanvasRenderingContext2D) => Promise<void>;
  getPdfPage?: (currentPage: number) => Promise<{
    view: [number, number, number, number];
  }>;
  getSAMImageDataPoints?: (input: SamModelImagePluginRequestT) => Promise<SAMSegmentImageData[]>;
  getSamSegmentData?: (input: SamModelPluginRequestT, labelTitle?: string) => Promise<SAMSegmentImageData | Error | null>;
  getAnnotationTrackingData?: (labelTitle: string, frameRange?: {
    start: number;
    end: number;
  }) => Promise<SAMSegmentVideoData | Error>;
  getSignedUrl?: GetSignedUrlT;
  getBoundingBoxData?: (input: BoundingBoxTrackingInputT) => Promise<BoundingBoxTrackingOutputT | Error>;
  trackObjectsAcrossKeyframes?: (input: TrackObjectsRequestT) => Promise<TrackObjectsOutputT | Error>;
  registerHotKeys?: (args: RegisterHotkeysArgsT) => RegisterHotkeysResultT;
  isSidePanelCollapsed: boolean;
  updatedQuestionnaire: boolean;
  suggestionSidePanelProps?: SuggestionSidePanelPropsT;
  studioRef: React.RefObject<StudioRef>;
  isReviewMode: boolean;
  logger?: Logger;
};
const StudioContainer = ({
  isSidePanelCollapsed,
  updatedQuestionnaire,
  suggestionSidePanelProps,
  studioRef,
  inputData,
  annotationLabels,
  onStoreChange,
  onTrackingActiveChange,
  sendEvent,
  fetchQuestionnaire,
  getSAMImageDataPoints,
  getSamSegmentData,
  getAnnotationTrackingData,
  getBoundingBoxData,
  trackObjectsAcrossKeyframes,
  getSignedUrl,
  registerHotKeys,
  isReviewMode,
  logger
}: StudioContainerPropsT) => {
  const featureFlags = getFeatureFlags();
  const feaureEnabledCallback = featureName => {
    return Promise.resolve({
      enabled: featureFlags[featureName]?.enabled ?? true
    });
  };
  const modernizedStudioInputData = useMemo(() => {
    if (isReviewMode) {
      return {
        ...inputData,
        taskState: {
          ...inputData.taskState,
          reviewModeVersion: REVIEW_MODE_VERSION_ULABEL_LITE,
          reviewFeatureConfig: DEFAULT_REVIEW_MODE_FEATURE_CONFIG_ULABEL_LITE
        }
      };
    }
    return {
      ...inputData
    };
  }, [inputData, isReviewMode]);
  return <ImageStudioContainer $isFullWidth={isSidePanelCollapsed || !(updatedQuestionnaire || suggestionSidePanelProps?.showSuggestionsPanel)} $showSuggestionsSidePanel={!!suggestionSidePanelProps?.showSuggestionsPanel}>
      <ImageLabelStudioModernized ref={studioRef} enableUndoRedoStageToolBar={false} enableStageToolBar={false} inputData={modernizedStudioInputData} annotationLabels={annotationLabels} onStoreChange={onStoreChange || (() => {})} onTrackingActiveChange={onTrackingActiveChange} sendEvent={sendEvent} logger={logger} fetchQuestionnaire={fetchQuestionnaire} setItemInStorage={setItemInStorage} getItemFromStorage={getItemFromStorage} getSuperpixelImageRegionsData={() => {
      throw new Error('Function not implemented.');
    }} isFeatureEnabled={feaureEnabledCallback} renderPDFFrame={async () => {}} getPdfPage={async () => ({
      view: [0, 0, 0, 0]
    })} getSAMImageDataPoints={getSAMImageDataPoints} getSamSegmentData={getSamSegmentData} getAnnotationTrackingData={getAnnotationTrackingData} getBoundingBoxData={getBoundingBoxData} trackObjectsAcrossKeyframes={trackObjectsAcrossKeyframes} getSignedUrl={getSignedUrl} registerHotKeys={registerHotKeys} enableKeyframeStepperTracking={featureFlags[FEATURE_FLAGS.ENABLE_KEYFRAME_STEPPER_TRACKING]?.enabled ?? false} />
    </ImageStudioContainer>;
};
export default StudioContainer;